import { Component, OnInit, OnDestroy, HostListener, TemplateRef, ViewChild, ElementRef } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, finalize, takeUntil } from 'rxjs/operators';
import { NgxSpinnerService } from 'ngx-spinner';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { InvoiceV2Service } from '../../../services/invoice-v2.service';
import { HttpHelpService } from '../../../Data/service/http-help.service';
import { CompaniesService } from '../../../components/alldocuments/alldocuments-companies/companies.service';
import { InvoiceSubscriptionsV2Service } from '../../../services/invoice-subscriptions-v2.service';
import { CurrencyService } from '../../../services/currency.service';
import { CurrencyDto, ExchangeRateDto } from '../../../models/currency.model';
import {
  FilterInvoicesV2Dto,
  InvoiceListResponseDto,
  InvoiceStatus,
  SortOrder,
  InvoiceType,
  InvoiceStatusCode,
  MultiLangMessagePayload,
  StoredPaymentStatus,
  UpdateInvoicePaymentBody,
} from '../../../models/invoice-v2.model';
import Swal from 'sweetalert2';
import { openCustomerWorkerNumberSwal } from '../worker-number-swal.util';
import { getDefaultReportLanguage } from 'src/app/shared/services/report-language-preference';
import {
  computeProcedureActionFlags,
  getProcedureLabel,
  isProcedureCycleConfirmResponse,
  isTruthyDbFlag,
  normalizeProcedureInvoiceRow,
} from '../invoice-v2-procedure.util';
import {
  applyPaymentFieldsToInvoiceRow,
  buildClearDueDatePatchBody,
  buildPaymentStatusPatchBody,
  buildUnpaidWithDueDatePatchBody,
  computeEffectivePaymentStatus,
  formatDueDateForApi,
  getEffectivePaymentStatusBadgeClass,
  getInvoicePaymentStatusLabel,
  isPaymentTrackingInvoiceType,
  needsDueDateBeforeUnpaid,
  parseDueDateFromApi,
  shouldShowPaymentTrackingInUi,
  shouldSkipPaymentStatusPatch,
} from '../invoice-v2-payment.util';
import {
  getDocumentCurrencySymbol,
  isForeignDocument,
  readSoldItemForeignFromApi,
  resolveDocumentCurrency,
  resolveForeignCurrencyFromInvoice,
} from '../invoice-v2-currency.util';
import { EffectivePaymentStatus } from '../../../models/invoice-v2.model';

export type DateFilterPresetId =
  | 'current_month'
  | 'last_month'
  | 'last_2_months'
  | 'last_3_months'
  | 'start_of_year'
  | 'last_year'
  | 'last_2_years'
  | 'subscription_period';

@Component({
  selector: 'app-invoices-v2-list',
  templateUrl: './invoices-v2-list.component.html',
  styleUrls: ['./invoices-v2-list.component.scss'],
})
export class InvoicesV2ListComponent implements OnInit, OnDestroy {
  curentLang: string = '';
  merchantId: number;
  user_type: number;

  // Data
  invoicesData: any[] = [];
  companyData: any[] = [];
  selectedCompany: any = null;
  allCompaniesOption = {
    company_id: null,
    company_name_en: 'All Companies',
    company_name_ar: 'الكل',
    company_name_he: 'הכל',
  };

  // Pagination
  currentPage: number = 1;
  limit: number = 25;
  offset: number = 0;
  total: number = 0;
  customLimit: number = 25;
  totalPages: number = 1;

  // Filters
  filters: FilterInvoicesV2Dto = {
    limit: 25,
    offset: 0,
    sort: SortOrder.DESC,
    sortIndex: 0,
  };
  /** PrimeNG calendar values; formatted to YYYY-MM-DD in `InvoiceV2Service`. */
  fromDate: Date | null = null;
  toDate: Date | null = null;
  dueDateFrom: Date | null = null;
  dueDateTo: Date | null = null;
  hasDueDate: boolean | null = null;
  selectedPaymentStatuses: EffectivePaymentStatus[] = [];
  readonly paymentStatusFilterOptions: EffectivePaymentStatus[] = [
    'paid',
    'unpaid',
    'due_soon',
    'overdue',
  ];
  /** Active quick-range chip id (see `datePresetOptions`). */
  activeDatePreset: DateFilterPresetId | null = null;
  readonly maxFilterDate = new Date();
  readonly datePresetGroups: { groupKey: 'months' | 'years'; presets: DateFilterPresetId[] }[] = [
    {
      groupKey: 'months',
      presets: ['current_month', 'last_month', 'last_2_months', 'last_3_months'],
    },
    {
      groupKey: 'years',
      presets: ['start_of_year', 'last_year', 'last_2_years'],
    },
  ];
  searchKeyword: string = '';
  selectedInvoiceTypes: number[] = [];
  selectedStatus: InvoiceStatus | null = null;
  /** When a single company is selected, filter by customer_id (GET /invoices-v2/filter). */
  selectedCustomerId: number | null = null;
  filterCustomers: { customer_id: number; customer_name_en?: string; customer_name_ar?: string; customer_name_he?: string }[] =
    [];
  minAmount: number | null = null;
  maxAmount: number | null = null;
  documentNumber: string = '';
  confirmationNumber: string = '';
  isInProcedureCycle: boolean | null = null;
  /** Document currency filter (USD/EUR/GBP/ILS) — maps to GET filter currency/currencies */
  selectedDocumentCurrencies: string[] = [];
  filterCurrencyOptions: CurrencyDto[] = [];

  // UI State
  isLoading: boolean = false;
  /** Export PDF / Excel in progress */
  isExporting: boolean = false;
  selectedInvoiceUuid: string | null = null;
  highlightedInvoiceId: number | null = null;
  private pendingHighlightInvoiceId: number | null = null;
  private highlightClearTimer: ReturnType<typeof setTimeout> | null = null;
  openedIndex: number | null = null;
  openedIndex2: number | null = null;

  // Invoice Types (loaded from backend)
  invoiceTypes: any[] = [];

  // Expose enums to template
  InvoiceStatus = InvoiceStatus;
  SortOrder = SortOrder;

  /** sortIndex 0–11 for API (see FilterInvoicesV2Dto). */
  readonly filterSortIndexOptions = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

  /**
   * Filters (without limit/offset) from the last successful `GET /invoices-v2/filter` response.
   * Export uses this so PDF/Excel match the current grid (same payload as list, minus pagination).
   */
  private filtersAppliedForListNoPagination: FilterInvoicesV2Dto | null = null;

  /** Collapsible filter accordion panels inside the filter card ([hidden] in template). */
  filterPanelExpanded: Record<'search' | 'scope' | 'period' | 'identifiers', boolean> = {
    search: false,
    scope: false,
    period: false,
    identifiers: false,
  };

  /** Active filter tab when the filter panel is open. */
  activeFilterTab: 'search' | 'scope' | 'period' | 'identifiers' = 'search';

  readonly filterTabs: {
    id: 'search' | 'scope' | 'period' | 'identifiers';
    icon: string;
    labelKey: string;
  }[] = [
    { id: 'search', icon: 'fa-search', labelKey: 'invoices_v2.list.filter_subsection_search' },
    { id: 'scope', icon: 'fa-building-o', labelKey: 'invoices_v2.list.filter_subsection_scope' },
    { id: 'period', icon: 'fa-calendar', labelKey: 'invoices_v2.list.filter_subsection_period' },
    { id: 'identifiers', icon: 'fa-sort-amount-desc', labelKey: 'invoices_v2.list.filter_subsection_identifiers' },
  ];

  /** Show/hide the filter panels card (toolbar stays visible with Reset). */
  filtersSectionExpanded = false;

  private readonly filterAutoApply$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();
  /** Customer id from URL — applied after company customers load on init. */
  private pendingCustomerIdFromUrl: number | null = null;

  canIssueDocumentAllowed: boolean = true;

  /** From GET /invoice-subscriptions-v2/status — shown beside list total for quota clarity */
  subscriptionDocumentsUsed: number | null = null;
  subscriptionPeriodStart: string | null = null;
  subscriptionPeriodEnd: string | null = null;

  /** Latest BOI exchange rates from GET /exchange-rates/current */
  currentExchangeRates: ExchangeRateDto[] = [];
  currentExchangeRatesAsOf: string | null = null;

  /** Explicit row IDs selected on the list (any type/company). Cleared when allFilteredSelected. */
  selectedListIds: number[] = [];
  /** When true, selection covers every invoice matching current filters (not just this page). */
  allFilteredSelected = false;

  @ViewChild('exportDateModal') exportDateModalTpl!: TemplateRef<unknown>;
  @ViewChild('procedureModal') procedureModalTpl!: TemplateRef<unknown>;

  /** Procedure cycle modal context */
  procedureModalInvoice: Record<string, unknown> | null = null;
  procedureModalInvoiceId: number | null = null;
  private activeProcedureModal: NgbModalRef | null = null;

  /** Inline payment editing in table rows */
  openPaymentStatusMenuId: number | null = null;
  openPaymentStatusMenuItem: any | null = null;
  paymentStatusMenuStyle: { top: string; left: string } | null = null;
  private paymentStatusAnchorEl: HTMLElement | null = null;
  private static readonly PAYMENT_STATUS_MENU_WIDTH = 160;
  private static readonly PAYMENT_STATUS_MENU_HEIGHT = 92;
  editingDueDateInvoiceId: number | null = null;
  /** Row being edited in the due-date popover. */
  editingDueDateItem: any | null = null;
  dueDatePopoverStyle: { top: string; left: string; maxHeight?: string } | null = null;
  private dueDateAnchorEl: HTMLElement | null = null;
  private static readonly DUE_DATE_POPOVER_WIDTH = 300;
  private static readonly DUE_DATE_POPOVER_ESTIMATED_HEIGHT = 420;
  @ViewChild('dueDatePopover') dueDatePopoverRef?: ElementRef<HTMLElement>;
  /** Row where user chose unpaid and must pick a due date before PATCH. */
  pendingUnpaidInvoiceId: number | null = null;
  dueDateEditValue: Date | null = null;
  savingPaymentInvoiceId: number | null = null;

  /** Open from toolbar: PDF or Excel */
  exportModalFormat: 'pdf' | 'excel' | null = null;
  exportModalFromDate: unknown = null;
  exportModalToDate: unknown = null;
  private activeExportModal: NgbModalRef | null = null;

  constructor(
    private invoiceV2Service: InvoiceV2Service,
    private companiesService: CompaniesService,
    private spinner: NgxSpinnerService,
    private translate: TranslateService,
    private toastr: ToastrService,
    private router: Router,
    private route: ActivatedRoute,
    private modalService: NgbModal,
    private httpHelpService: HttpHelpService,
    private invoiceSubscriptionsV2: InvoiceSubscriptionsV2Service,
    private currencyService: CurrencyService,
  ) {
    this.curentLang = localStorage.getItem('curentLang') || 'en';
    this.merchantId = JSON.parse(localStorage.getItem('merchant_id') || '0');
    this.user_type = JSON.parse(localStorage.getItem('user_type') || '0');
  }

  ngOnInit(): void {
    this.loadInvoiceTypes();

    const highlightRaw = this.route.snapshot.queryParamMap.get('highlightInvoiceId');
    if (highlightRaw != null && highlightRaw !== '') {
      const highlightId = +highlightRaw;
      if (Number.isFinite(highlightId) && highlightId > 0) {
        this.pendingHighlightInvoiceId = highlightId;
      }
    }

    this.applyFiltersFromQueryParams();
    this.loadCompanies();

    const ut = Number(this.user_type);
    if (ut === 100 || ut === 101) {
      this.invoiceSubscriptionsV2.canIssueDocument().subscribe({
        next: (res) => { this.canIssueDocumentAllowed = res.allowed; },
        error: () => { this.canIssueDocumentAllowed = true; },
      });
    }

    if (this.shouldLoadSubscriptionQuota()) {
      this.loadSubscriptionQuotaContext();
    }

    this.loadCurrentExchangeRates();
    this.loadFilterCurrencies();

    this.filterAutoApply$
      .pipe(debounceTime(400), takeUntil(this.destroy$))
      .subscribe(() => this.applyFilters());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.highlightClearTimer) {
      clearTimeout(this.highlightClearTimer);
      this.highlightClearTimer = null;
    }
  }

  scheduleFilterApply(): void {
    this.filterAutoApply$.next();
  }

  /** Restore list filters from URL (dashboard deep links + return from document preview). */
  private applyFiltersFromQueryParams(): void {
    const params = this.route.snapshot.queryParamMap;

    const fromDate = params.get('fromDate');
    const toDate = params.get('toDate');
    if (fromDate) {
      const parsed = this.parseQueryDate(fromDate);
      if (parsed) {
        this.fromDate = parsed;
      }
    }
    if (toDate) {
      const parsed = this.parseQueryDate(toDate);
      if (parsed) {
        this.toDate = parsed;
      }
    }
    if (this.fromDate || this.toDate) {
      this.activeDatePreset = null;
      this.filterPanelExpanded.period = true;
    }

    const dueDateFrom = params.get('dueDateFrom');
    const dueDateTo = params.get('dueDateTo');
    if (dueDateFrom) {
      const parsed = this.parseQueryDate(dueDateFrom);
      if (parsed) {
        this.dueDateFrom = parsed;
        this.filterPanelExpanded.period = true;
      }
    }
    if (dueDateTo) {
      const parsed = this.parseQueryDate(dueDateTo);
      if (parsed) {
        this.dueDateTo = parsed;
        this.filterPanelExpanded.period = true;
      }
    }

    const datePreset = params.get('datePreset');
    if (datePreset && this.isDatePresetId(datePreset)) {
      this.activeDatePreset = datePreset;
    }

    const invoiceTypesRaw = params.get('invoice_types') ?? params.get('invoiceTypes');
    const invoiceTypeRaw = params.get('invoiceType') ?? params.get('invoice_type');
    const parsedTypes = (invoiceTypesRaw ?? '')
      .split(',')
      .map((value) => value.trim())
      .filter((value) => value !== '')
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value));
    if (!parsedTypes.length && invoiceTypeRaw) {
      const parsedType = Number(invoiceTypeRaw);
      if (Number.isFinite(parsedType)) parsedTypes.push(parsedType);
    }
    if (parsedTypes.length) {
      this.selectedInvoiceTypes = parsedTypes;
      this.filterPanelExpanded.scope = true;
      this.activeFilterTab = 'scope';
    }

    const statusRaw = params.get('status');
    if (statusRaw !== null && statusRaw !== '') {
      const parsedStatus = Number(statusRaw);
      if (Number.isFinite(parsedStatus)) {
        this.selectedStatus = parsedStatus as InvoiceStatus;
        this.filterPanelExpanded.scope = true;
      }
    }

    const customerIdRaw = params.get('customerId');
    if (customerIdRaw) {
      const parsedCustomer = Number(customerIdRaw);
      if (Number.isFinite(parsedCustomer) && parsedCustomer > 0) {
        this.pendingCustomerIdFromUrl = parsedCustomer;
      }
    }

    const hasDueDateRaw = params.get('hasDueDate');
    if (hasDueDateRaw === 'true') {
      this.hasDueDate = true;
      this.filterPanelExpanded.identifiers = true;
    } else if (hasDueDateRaw === 'false') {
      this.hasDueDate = false;
      this.filterPanelExpanded.identifiers = true;
    }

    const paymentStatusesRaw = params.get('paymentStatuses');
    if (paymentStatusesRaw) {
      this.selectedPaymentStatuses = paymentStatusesRaw
        .split(',')
        .map((s) => s.trim())
        .filter((s): s is EffectivePaymentStatus =>
          this.paymentStatusFilterOptions.includes(s as EffectivePaymentStatus),
        );
      if (this.selectedPaymentStatuses.length) {
        this.filterPanelExpanded.identifiers = true;
      }
    }

    const search = params.get('search');
    if (search) {
      this.searchKeyword = search;
      this.filterPanelExpanded.search = true;
    }

    const minAmountRaw = params.get('minAmount');
    if (minAmountRaw) {
      const parsed = Number(minAmountRaw);
      if (Number.isFinite(parsed)) {
        this.minAmount = parsed;
        this.filterPanelExpanded.period = true;
      }
    }

    const maxAmountRaw = params.get('maxAmount');
    if (maxAmountRaw) {
      const parsed = Number(maxAmountRaw);
      if (Number.isFinite(parsed)) {
        this.maxAmount = parsed;
        this.filterPanelExpanded.period = true;
      }
    }

    const documentNumber = params.get('documentNumber');
    if (documentNumber) {
      this.documentNumber = documentNumber;
      this.filterPanelExpanded.identifiers = true;
    }

    const confirmationNumber = params.get('confirmationNumber');
    if (confirmationNumber) {
      this.confirmationNumber = confirmationNumber;
      this.filterPanelExpanded.identifiers = true;
    }

    const procedureRaw = params.get('isInProcedureCycle');
    if (procedureRaw === 'true') {
      this.isInProcedureCycle = true;
      this.filterPanelExpanded.identifiers = true;
    } else if (procedureRaw === 'false') {
      this.isInProcedureCycle = false;
      this.filterPanelExpanded.identifiers = true;
    }

    const currenciesRaw = params.get('currencies');
    if (currenciesRaw) {
      this.selectedDocumentCurrencies = currenciesRaw
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean);
      if (this.selectedDocumentCurrencies.length) {
        this.filterPanelExpanded.scope = true;
      }
    }

    const sortRaw = params.get('sort');
    if (sortRaw === SortOrder.ASC || sortRaw === SortOrder.DESC) {
      this.filters.sort = sortRaw;
    }

    const sortIndexRaw = params.get('sortIndex');
    if (sortIndexRaw !== null && sortIndexRaw !== '') {
      const parsedSortIndex = Number(sortIndexRaw);
      if (Number.isFinite(parsedSortIndex)) {
        this.filters.sortIndex = parsedSortIndex;
      }
    }

    const pageRaw = params.get('page');
    if (pageRaw) {
      const parsedPage = Number(pageRaw);
      if (Number.isFinite(parsedPage) && parsedPage >= 1) {
        this.currentPage = parsedPage;
        this.offset = (parsedPage - 1) * this.limit;
      }
    }

    const limitRaw = params.get('limit');
    if (limitRaw) {
      const parsedLimit = Number(limitRaw);
      if (Number.isFinite(parsedLimit) && parsedLimit >= 1) {
        this.limit = Math.min(100, parsedLimit);
        this.customLimit = this.limit;
        this.offset = (this.currentPage - 1) * this.limit;
      }
    }

    if (this.countActiveFilters() > 0) {
      this.filtersSectionExpanded = true;
    }
  }

  private buildFilterQueryParams(): Record<string, string | number | null> {
    const q: Record<string, string | number | null> = {};

    q['companyId'] = this.selectedCompany?.company_id ?? null;
    q['customerId'] = this.selectedCustomerId ?? null;
    q['invoiceType'] = this.selectedInvoiceTypes.length === 1 ? this.selectedInvoiceTypes[0] : null;
    q['invoice_types'] = this.selectedInvoiceTypes.length > 1 ? this.selectedInvoiceTypes.join(',') : null;
    q['status'] = this.selectedStatus ?? null;
    q['fromDate'] = this.formatQueryDate(this.fromDate);
    q['toDate'] = this.formatQueryDate(this.toDate);
    q['dueDateFrom'] = this.formatQueryDate(this.dueDateFrom);
    q['dueDateTo'] = this.formatQueryDate(this.dueDateTo);
    q['hasDueDate'] =
      this.hasDueDate === true ? 'true' : this.hasDueDate === false ? 'false' : null;
    q['paymentStatuses'] = this.selectedPaymentStatuses.length
      ? this.selectedPaymentStatuses.join(',')
      : null;
    q['search'] = this.searchKeyword.trim() || null;
    q['minAmount'] = this.minAmount ?? null;
    q['maxAmount'] = this.maxAmount ?? null;
    q['documentNumber'] = this.documentNumber.trim() || null;
    q['confirmationNumber'] = this.confirmationNumber.trim() || null;
    q['isInProcedureCycle'] =
      this.isInProcedureCycle === true
        ? 'true'
        : this.isInProcedureCycle === false
          ? 'false'
          : null;
    q['currencies'] = this.selectedDocumentCurrencies.length
      ? this.selectedDocumentCurrencies.join(',')
      : null;
    q['sort'] = this.filters.sort ?? SortOrder.DESC;
    q['sortIndex'] = this.filters.sortIndex ?? 0;
    q['datePreset'] = this.activeDatePreset ?? null;
    q['page'] = this.currentPage > 1 ? this.currentPage : null;
    q['limit'] = this.limit !== 25 ? this.limit : null;

    const highlight = this.route.snapshot.queryParamMap.get('highlightInvoiceId');
    q['highlightInvoiceId'] = highlight ?? null;

    return q;
  }

  private syncFiltersToUrl(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.buildFilterQueryParams(),
      replaceUrl: true,
    });
  }

  private parseQueryDate(raw: string): Date | null {
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private formatQueryDate(value: Date | null): string | null {
    if (!value) {
      return null;
    }
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private isDatePresetId(value: string): value is DateFilterPresetId {
    return [
      'current_month',
      'last_month',
      'last_2_months',
      'last_3_months',
      'start_of_year',
      'last_year',
      'last_2_years',
      'subscription_period',
    ].includes(value);
  }

  shouldLoadSubscriptionQuota(): boolean {
    const ut = Number(this.user_type);
    return [100, 101, 300, 301].includes(ut) && !!this.merchantId;
  }

  loadSubscriptionQuotaContext(): void {
    this.invoiceSubscriptionsV2.getStatus().subscribe({
      next: (res) => {
        const sub = res?.subscription;
        if (sub) {
          this.subscriptionDocumentsUsed = sub.documents_used ?? null;
          this.subscriptionPeriodStart = sub.start_date ?? null;
          this.subscriptionPeriodEnd = sub.end_date ?? null;
        }
      },
      error: () => {
        // Quota hint is optional — list still works without it
      },
    });
  }

  private loadCurrentExchangeRates(): void {
    this.currencyService.getCurrentExchangeRates().subscribe({
      next: (data) => {
        const rates = data.rates ?? [];
        this.currentExchangeRates = rates.filter((r) => r.currency_code !== 'ILS');
        this.currentExchangeRatesAsOf = data.as_of || this.currentExchangeRates[0]?.exchange_rate_date || null;
      },
      error: () => {
        this.currentExchangeRates = [];
        this.currentExchangeRatesAsOf = null;
      },
    });
  }

  private loadFilterCurrencies(): void {
    this.currencyService.getActiveCurrencies().subscribe({
      next: (list) => {
        this.filterCurrencyOptions = [...list].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
      },
      error: () => {
        this.filterCurrencyOptions = [];
      },
    });
  }

  filterCurrencyLabel(code: string): string {
    const match = this.filterCurrencyOptions.find((c) => c.currency_code === code);
    if (!match) {
      return code;
    }
    if (this.curentLang === 'ar') {
      return `${match.currency_symbol} ${match.name_ar || match.currency_code}`;
    }
    if (this.curentLang === 'he') {
      return `${match.currency_symbol} ${match.name_he || match.currency_code}`;
    }
    return `${match.currency_symbol} ${match.name_en || match.currency_code}`;
  }

  hasSubscriptionPeriod(): boolean {
    return !!(this.subscriptionPeriodStart && this.subscriptionPeriodEnd);
  }

  formatSubscriptionPeriodDate(dateStr: string | null): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }

  applySubscriptionPeriodFilter(): void {
    if (!this.subscriptionPeriodStart || !this.subscriptionPeriodEnd) return;
    this.fromDate = new Date(this.subscriptionPeriodStart);
    this.toDate = new Date(this.subscriptionPeriodEnd);
    this.activeDatePreset = 'subscription_period';
    this.applyFilters();
  }

  onTextFilterChange(): void {
    this.scheduleFilterApply();
  }

  onAmountFilterChange(): void {
    this.scheduleFilterApply();
  }

  onDueDateChange(): void {
    this.scheduleFilterApply();
  }

  toggleFilterPanel(panel: 'search' | 'scope' | 'period' | 'identifiers'): void {
    this.filterPanelExpanded[panel] = !this.filterPanelExpanded[panel];
  }

  setActiveFilterTab(tab: 'search' | 'scope' | 'period' | 'identifiers'): void {
    this.activeFilterTab = tab;
  }

  countFilterSection(section: 'search' | 'scope' | 'period' | 'identifiers'): number {
    switch (section) {
      case 'search':
        return this.searchKeyword?.trim() ? 1 : 0;
      case 'scope': {
        let count = 0;
        if (this.selectedCompany?.company_id) count++;
        if (this.selectedCustomerId != null) count++;
        if (this.selectedInvoiceTypes.length) count++;
        if (this.selectedStatus != null) count++;
        if (this.selectedDocumentCurrencies?.length) count++;
        return count;
      }
      case 'period': {
        let count = 0;
        if (this.fromDate || this.toDate) count++;
        if (this.minAmount != null || this.maxAmount != null) count++;
        if (this.dueDateFrom || this.dueDateTo) count++;
        return count;
      }
      case 'identifiers': {
        let count = 0;
        if (this.documentNumber?.trim()) count++;
        if (this.confirmationNumber?.trim()) count++;
        if (this.isInProcedureCycle != null) count++;
        if (this.selectedPaymentStatuses?.length) count++;
        if (this.hasDueDate != null) count++;
        if (this.filters.sortIndex !== 0 || this.filters.sort !== SortOrder.DESC) count++;
        return count;
      }
      default:
        return 0;
    }
  }

  countActiveFilters(): number {
    return (
      this.countFilterSection('search') +
      this.countFilterSection('scope') +
      this.countFilterSection('period') +
      this.countFilterSection('identifiers')
    );
  }

  private getFirstTabWithActiveFilters(): 'search' | 'scope' | 'period' | 'identifiers' | null {
    for (const tab of this.filterTabs) {
      if (this.countFilterSection(tab.id) > 0) {
        return tab.id;
      }
    }
    return null;
  }

  toggleFiltersSection(): void {
    this.filtersSectionExpanded = !this.filtersSectionExpanded;
    if (this.filtersSectionExpanded) {
      this.activeFilterTab = this.getFirstTabWithActiveFilters() ?? 'search';
    }
  }

  closeFiltersSection(): void {
    this.filtersSectionExpanded = false;
  }

  expandAllFilterPanels(): void {
    const keys: Array<'search' | 'scope' | 'period' | 'identifiers'> = ['search', 'scope', 'period', 'identifiers'];
    keys.forEach((k) => {
      this.filterPanelExpanded[k] = true;
    });
  }

  collapseAllFilterPanels(): void {
    const keys: Array<'search' | 'scope' | 'period' | 'identifiers'> = ['search', 'scope', 'period', 'identifiers'];
    keys.forEach((k) => {
      this.filterPanelExpanded[k] = false;
    });
  }

  /**
   * Load invoice types from backend
   */
  loadInvoiceTypes(): void {
    this.httpHelpService.GetInvoiceTypeList().subscribe({
      next: (response: any) => {
        let typesData: any[] = [];
        if (Array.isArray(response)) {
          typesData = response;
        } else if (response && Array.isArray(response.data)) {
          typesData = response.data;
        }

        this.invoiceTypes = typesData
          .filter((t: any) => t.status === 1)
          .map((t: any) => ({
            id: String(t.invoice_type_code),
            name_en: t.invoice_type_name_en,
            name_ar: t.invoice_type_name_ar,
            name_he: t.invoice_type_name_he,
            has_items: t.has_items,
            has_payments: t.has_payments,
            can_convert: t.can_convert,
            convert_to_invoice_type_code: t.convert_to_invoice_type_code,
          }));
      },
      error: (error) => {
        console.error('Error loading invoice types:', error);
      },
    });
  }

  /**
   * Load companies for filter dropdown
   */
  loadCompanies(): void {
    this.companiesService.getAllCompanies().subscribe({
      next: (companies) => {
        this.companyData = companies.map((c: any) => ({
          company_id: c.code,
          company_name_en: c.name,
          company_name_ar: c.fullData?.company_name_ar || c.name,
          company_name_he: c.fullData?.company_name_he || c.name,
          company_status: c.fullData?.company_status ?? 'ACTIVE',
        }));

        const companyIdRaw = this.route.snapshot.queryParamMap.get('companyId');
        if (companyIdRaw) {
          const cid = Number(companyIdRaw);
          if (Number.isFinite(cid) && cid > 0) {
            this.selectedCompany =
              this.companyData.find((c) => Number(c.company_id) === cid) ?? this.allCompaniesOption;
          } else {
            this.selectedCompany = this.allCompaniesOption;
          }
        } else {
          this.selectedCompany = this.allCompaniesOption;
        }

        this.finishInitialFilterLoad();
      },
      error: (error) => {
        console.error('Error loading companies:', error);
        this.selectedCompany = this.allCompaniesOption;
        this.finishInitialFilterLoad();
      },
    });
  }

  /** After companies (and optional customers) resolve, load the filtered grid once. */
  private finishInitialFilterLoad(): void {
    const cid = this.selectedCompany?.company_id;
    if (!cid) {
      this.filterCustomers = [];
      if (this.pendingCustomerIdFromUrl != null) {
        this.selectedCustomerId = this.pendingCustomerIdFromUrl;
        this.pendingCustomerIdFromUrl = null;
      }
      this.loadInvoices();
      return;
    }

    this.httpHelpService.getCustomersByCompanyId(+cid).subscribe({
      next: (response: any) => {
        this.filterCustomers = this.mapFilterCustomersResponse(response);
        if (this.pendingCustomerIdFromUrl != null) {
          this.selectedCustomerId = this.pendingCustomerIdFromUrl;
          this.pendingCustomerIdFromUrl = null;
        }
        this.loadInvoices();
      },
      error: () => {
        this.filterCustomers = [];
        if (this.pendingCustomerIdFromUrl != null) {
          this.selectedCustomerId = this.pendingCustomerIdFromUrl;
          this.pendingCustomerIdFromUrl = null;
        }
        this.loadInvoices();
      },
    });
  }

  private mapFilterCustomersResponse(response: any): {
    customer_id: number;
    customer_name_en?: string;
    customer_name_ar?: string;
    customer_name_he?: string;
  }[] {
    let list: any[] = [];
    if (Array.isArray(response)) {
      list = response;
    } else if (response?.customers && Array.isArray(response.customers)) {
      list = response.customers;
    } else if (response?.data && Array.isArray(response.data)) {
      list = response.data;
    }
    return list
      .map((c: any) => ({
        customer_id: c.customer_id ?? c.customer_customer_id,
        customer_name_en: c.customer_name_en || c.name,
        customer_name_ar: c.customer_name_ar,
        customer_name_he: c.customer_name_he,
      }))
      .filter((c) => c.customer_id != null);
  }

  /**
   * Customers for filter dropdown (requires a single company).
   */
  loadFilterCustomers(): void {
    const cid = this.selectedCompany?.company_id;
    if (!cid) {
      this.filterCustomers = [];
      this.selectedCustomerId = null;
      return;
    }
    this.httpHelpService.getCustomersByCompanyId(+cid).subscribe({
      next: (response: any) => {
        this.filterCustomers = this.mapFilterCustomersResponse(response);
      },
      error: () => {
        this.filterCustomers = [];
      },
    });
  }

  onCompanyFilterChange(): void {
    this.selectedCustomerId = null;
    this.loadFilterCustomers();
    this.applyFilters();
  }

  filterCustomerLabel(c: {
    customer_id: number;
    customer_name_en?: string;
    customer_name_ar?: string;
    customer_name_he?: string;
  }): string {
    if (this.curentLang === 'ar') return c.customer_name_ar || c.customer_name_en || String(c.customer_id);
    if (this.curentLang === 'he') return c.customer_name_he || c.customer_name_en || String(c.customer_id);
    return c.customer_name_en || c.customer_name_ar || String(c.customer_id);
  }

  /**
   * Build filter DTO aligned with GET /invoices-v2/filter (and export endpoints).
   * Only includes fields that are actually constrained in the UI (no stale keys from older requests).
   */
  private getFiltersSnapshot(includePagination: boolean): FilterInvoicesV2Dto {
    const filters: FilterInvoicesV2Dto = {
      sort: this.filters.sort ?? SortOrder.DESC,
      sortIndex: this.filters.sortIndex ?? 0,
    };

    if (includePagination) {
      filters.limit = this.limit;
      filters.offset = this.offset;
    }

    if (this.fromDate) filters.fromDate = this.fromDate as unknown as FilterInvoicesV2Dto['fromDate'];
    if (this.toDate) filters.toDate = this.toDate as unknown as FilterInvoicesV2Dto['toDate'];
    if (this.dueDateFrom) {
      filters.dueDateFrom = this.dueDateFrom as unknown as FilterInvoicesV2Dto['dueDateFrom'];
    }
    if (this.dueDateTo) {
      filters.dueDateTo = this.dueDateTo as unknown as FilterInvoicesV2Dto['dueDateTo'];
    }
    if (this.hasDueDate !== null && this.hasDueDate !== undefined) {
      filters.has_due_date = this.hasDueDate;
    }
    if (this.selectedPaymentStatuses?.length === 1) {
      filters.payment_status = this.selectedPaymentStatuses[0];
    } else if (this.selectedPaymentStatuses?.length > 1) {
      filters.payment_statuses = this.selectedPaymentStatuses.join(',');
    }

    const companyId = this.selectedCompany?.company_id;
    if (companyId !== undefined && companyId !== null && companyId !== '') {
      filters.companyId = +companyId;
    }

    if (this.selectedCustomerId != null && !Number.isNaN(Number(this.selectedCustomerId))) {
      filters.customer_id = Number(this.selectedCustomerId);
    }

    if (this.selectedInvoiceTypes.length === 1) {
      filters.invoice_type = this.selectedInvoiceTypes[0];
    } else if (this.selectedInvoiceTypes.length > 1) {
      filters.invoice_types = [...this.selectedInvoiceTypes];
    }
    if (this.selectedStatus !== null && this.selectedStatus !== undefined) {
      filters.status = this.selectedStatus;
    }
    if (this.searchKeyword?.trim()) {
      filters.searchKeyword = this.searchKeyword.trim();
    }
    if (this.minAmount !== null && this.minAmount !== undefined && !Number.isNaN(Number(this.minAmount))) {
      filters.minAmount = Number(this.minAmount);
    }
    if (this.maxAmount !== null && this.maxAmount !== undefined && !Number.isNaN(Number(this.maxAmount))) {
      filters.maxAmount = Number(this.maxAmount);
    }
    if (this.documentNumber?.trim()) {
      filters.document_number = this.documentNumber.trim();
    }
    if (this.confirmationNumber?.trim()) {
      filters.confirmation_number = this.confirmationNumber.trim();
    }
    if (this.isInProcedureCycle !== null && this.isInProcedureCycle !== undefined) {
      filters.is_in_procedure_cycle = this.isInProcedureCycle;
    }

    if (this.selectedDocumentCurrencies?.length === 1) {
      filters.currency = this.selectedDocumentCurrencies[0];
    } else if (this.selectedDocumentCurrencies?.length > 1) {
      filters.currencies = this.selectedDocumentCurrencies.join(',');
    }

    return filters;
  }

  private cloneFiltersWithoutPagination(filters: FilterInvoicesV2Dto): FilterInvoicesV2Dto {
    const copy = { ...filters };
    delete copy.limit;
    delete copy.offset;
    return copy;
  }

  /** Filters for export: same as last successful list load (matches grid); fallback before first load completes. */
  private getExportFiltersPayload(): FilterInvoicesV2Dto {
    if (this.filtersAppliedForListNoPagination != null) {
      return { ...this.filtersAppliedForListNoPagination };
    }
    return this.getFiltersSnapshot(false);
  }

  /**
   * Export list as PDF or Excel. Pass `filtersPayload` from the date modal or omit to use last-applied list filters.
   */
  exportInvoices(format: 'pdf' | 'excel', filtersPayload?: FilterInvoicesV2Dto): void {
    if (this.isExporting) return;
    this.isExporting = true;
    this.spinner.show();

    const lang = getDefaultReportLanguage();
    const filters = filtersPayload ?? this.getExportFiltersPayload();
    const fallbackName =
      format === 'pdf'
        ? `invoices-${new Date().toISOString().slice(0, 10)}.pdf`
        : `invoices-${new Date().toISOString().slice(0, 10)}.xlsx`;

    const req$ =
      format === 'pdf'
        ? this.invoiceV2Service.exportFilteredInvoicesPdf(filters, lang)
        : this.invoiceV2Service.exportFilteredInvoicesExcel(filters, lang);

    req$
      .pipe(
        finalize(() => {
          this.isExporting = false;
          this.spinner.hide();
        }),
      )
      .subscribe({
        next: async (resp) => {
          const blob = resp.body;
          if (!blob?.size) {
            this.toastr.error(this.translate.instant('invoices_v2.list.export_failed'));
            return;
          }
          const ctype = resp.headers.get('Content-Type') || '';
          if (ctype.includes('application/json')) {
            const text = await blob.text();
            try {
              const j = JSON.parse(text);
              this.toastr.error(j.message || this.translate.instant('invoices_v2.list.export_failed'));
            } catch {
              this.toastr.error(this.translate.instant('invoices_v2.list.export_failed'));
            }
            return;
          }
          const name =
            this.filenameFromContentDisposition(resp.headers.get('Content-Disposition')) || fallbackName;
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = name;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          this.toastr.success(this.translate.instant('invoices_v2.list.export_success'));
        },
        error: async (err: HttpErrorResponse) => {
          let msg = this.translate.instant('invoices_v2.list.export_failed');
          if (err.error instanceof Blob) {
            try {
              const text = await err.error.text();
              const j = JSON.parse(text);
              if (j.message) msg = j.message;
            } catch {
              /* keep default */
            }
          }
          this.toastr.error(msg);
        },
      });
  }

  openExportModal(format: 'pdf' | 'excel'): void {
    if (this.isExporting) return;
    this.exportModalFormat = format;
    const applied = this.filtersAppliedForListNoPagination;
    this.exportModalFromDate = this.firstDefinedDate(applied?.fromDate, this.fromDate);
    this.exportModalToDate = this.firstDefinedDate(applied?.toDate, this.toDate);

    this.activeExportModal = this.modalService.open(this.exportDateModalTpl, {
      centered: true,
      size: 'md',
      backdrop: 'static',
    });
    this.activeExportModal.result.finally(() => {
      this.activeExportModal = null;
      this.exportModalFormat = null;
    });
  }

  /** Toolbar PDF: selection export when rows are selected; otherwise date-range modal. */
  onToolbarPdfClick(): void {
    if (this.hasListSelection) {
      this.exportSelectedPdf();
    } else {
      this.openExportModal('pdf');
    }
  }

  /** Toolbar Excel: selection export when rows are selected; otherwise date-range modal. */
  onToolbarExcelClick(): void {
    if (this.hasListSelection) {
      this.exportSelectedExcel();
    } else {
      this.openExportModal('excel');
    }
  }

  exportSelectedPdf(): void {
    if (!this.hasListSelection || this.isExporting) return;
    if (this.allFilteredSelected) {
      this.exportInvoices('pdf');
      return;
    }
    this.downloadMergedPdfForIds(this.selectedListIds);
  }

  exportSelectedExcel(): void {
    if (!this.hasListSelection || this.isExporting) return;
    if (this.allFilteredSelected) {
      this.exportInvoices('excel');
      return;
    }
    const filters: FilterInvoicesV2Dto = {
      ...this.getExportFiltersPayload(),
      invoice_ids: [...this.selectedListIds],
    };
    this.exportInvoices('excel', filters);
  }

  printSelectedDocuments(): void {
    if (!this.hasListSelection || this.isExporting) return;
    this.isExporting = true;
    this.spinner.show();
    const lang = getDefaultReportLanguage();

    const finish = () => {
      this.isExporting = false;
      this.spinner.hide();
    };

    const openPrint = (blob: Blob | null) => {
      finish();
      if (!blob?.size) {
        this.toastr.error(this.translate.instant('invoices_v2.list.export_failed'));
        return;
      }
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank');
      if (!win) {
        this.toastr.warning(this.translate.instant('invoices_v2.list.print_popup_blocked'));
        const a = document.createElement('a');
        a.href = url;
        a.download = `invoices-print-${new Date().toISOString().slice(0, 10)}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        return;
      }
      const revoke = () => URL.revokeObjectURL(url);
      win.addEventListener('load', () => {
        try {
          win.focus();
          win.print();
        } catch {
          /* browser may block auto-print; user can print manually */
        }
        setTimeout(revoke, 60_000);
      });
      // Fallback revoke if load never fires (some browsers)
      setTimeout(revoke, 120_000);
    };

    if (this.allFilteredSelected) {
      this.invoiceV2Service.exportFilteredInvoicesPdf(this.getExportFiltersPayload(), lang).subscribe({
        next: (resp) => openPrint(resp.body),
        error: async (err: HttpErrorResponse) => {
          finish();
          let msg = this.translate.instant('invoices_v2.list.export_failed');
          if (err.error instanceof Blob) {
            try {
              const text = await err.error.text();
              const j = JSON.parse(text);
              if (j.message) msg = j.message;
            } catch {
              /* keep default */
            }
          }
          this.toastr.error(msg);
        },
      });
    } else {
      this.invoiceV2Service.exportMultipleInvoicesPdf(this.selectedListIds).subscribe({
        next: (blob) => openPrint(blob),
        error: (error) => {
          finish();
          this.toastr.error(this.getApiErrorMessage(error, 'Error printing documents'));
        },
      });
    }
  }

  private downloadMergedPdfForIds(invoiceIds: number[]): void {
    if (!invoiceIds.length || this.isExporting) return;
    this.isExporting = true;
    this.spinner.show();
    this.invoiceV2Service.exportMultipleInvoicesPdf(invoiceIds).subscribe({
      next: (blob: Blob) => {
        this.isExporting = false;
        this.spinner.hide();
        if (!blob?.size) {
          this.toastr.error(this.translate.instant('invoices_v2.list.export_failed'));
          return;
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `invoices_${invoiceIds.join('_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        this.toastr.success(this.translate.instant('invoices_v2.list.export_success'));
      },
      error: (error) => {
        this.isExporting = false;
        this.spinner.hide();
        this.toastr.error(this.getApiErrorMessage(error, 'Error downloading PDF'));
      },
    });
  }

  private firstDefinedDate(...candidates: unknown[]): unknown {
    for (const v of candidates) {
      if (v != null && v !== '') return v;
    }
    return null;
  }

  confirmExportModal(): void {
    const hasFrom = this.exportModalFromDate != null && this.exportModalFromDate !== '';
    const hasTo = this.exportModalToDate != null && this.exportModalToDate !== '';
    if (hasFrom !== hasTo) {
      this.toastr.warning(this.translate.instant('invoices_v2.list.export_dates_both_or_none'));
      return;
    }

    const base = this.getExportFiltersPayload();
    const merged: FilterInvoicesV2Dto = { ...base };
    if (hasFrom && hasTo) {
      merged.fromDate = this.exportModalFromDate as FilterInvoicesV2Dto['fromDate'];
      merged.toDate = this.exportModalToDate as FilterInvoicesV2Dto['toDate'];
    } else {
      delete merged.fromDate;
      delete merged.toDate;
    }

    const fmt = this.exportModalFormat;
    this.activeExportModal?.close();
    if (fmt) {
      this.exportInvoices(fmt, merged);
    }
  }

  exportModalTitleKey(): string {
    return this.exportModalFormat === 'excel'
      ? 'invoices_v2.list.export_modal_title_excel'
      : 'invoices_v2.list.export_modal_title_pdf';
  }

  private filenameFromContentDisposition(header: string | null): string | null {
    if (!header) return null;
    const star = /filename\*=(?:UTF-8'')?([^;\n]+)/i.exec(header);
    if (star) {
      try {
        return decodeURIComponent(star[1].trim().replace(/^"(.*)"$/, '$1'));
      } catch {
        return star[1].trim().replace(/^"(.*)"$/, '$1');
      }
    }
    const quoted = /filename="([^"]+)"/i.exec(header);
    if (quoted) return quoted[1];
    const plain = /filename=([^;\s]+)/i.exec(header);
    return plain ? plain[1].replace(/^"(.*)"$/, '$1') : null;
  }

  /**
   * Load invoices with current filters
   */
  loadInvoices(): void {
    this.isLoading = true;
    this.spinner.show();

    this.limit = Math.min(100, Math.max(1, Number(this.limit) || 10));
    this.offset = Math.max(0, Number(this.offset) || 0);
    this.customLimit = this.limit;

    const filters = this.getFiltersSnapshot(true);

    this.invoiceV2Service.getFilteredInvoices(filters).subscribe({
      next: (response: InvoiceListResponseDto) => {
        this.filtersAppliedForListNoPagination = this.cloneFiltersWithoutPagination(filters);
        this.invoicesData = (response.data || []).map((row: Record<string, unknown>) =>
          normalizeProcedureInvoiceRow(row),
        );
        this.total = response.total || 0;
        if (response.limit != null) {
          this.limit = Math.min(100, Math.max(1, +response.limit));
          this.customLimit = this.limit;
        }
        if (response.offset != null) {
          this.offset = Math.max(0, +response.offset);
        }
        const pageFromApi = response.page;
        this.currentPage =
          pageFromApi != null && !isNaN(+pageFromApi)
            ? +pageFromApi
            : this.limit > 0
              ? Math.floor(this.offset / this.limit) + 1
              : 1;
        this.totalPages = Math.ceil(this.total / this.limit) || 1;
        this.isLoading = false;
        this.spinner.hide();
        this.maybeHighlightInvoiceRow();
      },
      error: (error) => {
        console.error('Error loading invoices:', error);
        this.isLoading = false;
        this.spinner.hide();
        this.toastr.error(this.getApiErrorMessage(error, 'Error loading invoices'));
      },
    });
  }

  /**
   * Apply filters and reload
   */
  applyFilters(): void {
    this.clearListSelection();
    this.currentPage = 1;
    this.offset = 0;
    this.loadInvoices();
    this.syncFiltersToUrl();
  }

  /**
   * Reset all filters
   */
  applyDatePreset(preset: DateFilterPresetId): void {
    const today = new Date();
    let start: Date;
    let end: Date;

    switch (preset) {
      case 'current_month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case 'last_month':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'last_2_months':
        start = new Date(today.getFullYear(), today.getMonth() - 2, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'last_3_months':
        start = new Date(today.getFullYear(), today.getMonth() - 3, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'start_of_year':
        start = new Date(today.getFullYear(), 0, 1);
        end = today;
        break;
      case 'last_year':
        start = new Date(today.getFullYear() - 1, 0, 1);
        end = new Date(today.getFullYear() - 1, 11, 31);
        break;
      case 'last_2_years':
        start = new Date(today.getFullYear() - 1, 0, 1);
        end = new Date(today.getFullYear(), 11, 31);
        break;
      case 'subscription_period':
        if (this.subscriptionPeriodStart && this.subscriptionPeriodEnd) {
          this.fromDate = new Date(this.subscriptionPeriodStart);
          this.toDate = new Date(this.subscriptionPeriodEnd);
          this.activeDatePreset = preset;
          this.applyFilters();
        }
        return;
    }

    this.fromDate = start;
    this.toDate = end;
    this.activeDatePreset = preset;
    this.applyFilters();
  }

  datePresetLabelKey(preset: DateFilterPresetId): string {
    return `invoices_v2.list.date_preset_${preset}`;
  }

  onManualDateChange(): void {
    this.activeDatePreset = null;
    this.scheduleFilterApply();
  }

  clearDateRange(): void {
    this.fromDate = null;
    this.toDate = null;
    this.activeDatePreset = null;
    this.applyFilters();
  }

  clearDueDateRange(): void {
    this.dueDateFrom = null;
    this.dueDateTo = null;
    this.applyFilters();
  }

  resetFilters(): void {
    this.fromDate = null;
    this.toDate = null;
    this.dueDateFrom = null;
    this.dueDateTo = null;
    this.hasDueDate = null;
    this.selectedPaymentStatuses = [];
    this.activeDatePreset = null;
    this.searchKeyword = '';
    this.selectedInvoiceTypes = [];
    this.selectedStatus = null;
    this.selectedCustomerId = null;
    this.minAmount = null;
    this.maxAmount = null;
    this.documentNumber = '';
    this.confirmationNumber = '';
    this.isInProcedureCycle = null;
    this.selectedDocumentCurrencies = [];
    this.filters.sort = SortOrder.DESC;
    this.filters.sortIndex = 0;
    this.selectedCompany = this.allCompaniesOption;
    this.filterCustomers = [];
    this.applyFilters();
  }

  /**
   * Handle pagination
   */
  onPageChange(page: number): void {
    this.currentPage = page;
    this.offset = (page - 1) * this.limit;
    this.loadInvoices();
    this.syncFiltersToUrl();
  }

  /**
   * Handle limit change
   */
  onLimitChange(): void {
    this.limit = Math.min(100, Math.max(1, Number(this.customLimit) || 10));
    this.customLimit = this.limit;
    this.offset = 0;
    this.currentPage = 1;
    this.totalPages = Math.ceil(this.total / this.limit) || 1;
    this.loadInvoices();
    this.syncFiltersToUrl();
  }

  /**
   * Get page numbers for pagination display
   */
  getPageNumbers(): (number | string)[] {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;
    
    if (this.totalPages <= maxVisiblePages + 2) {
      // Show all pages if total is small
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      if (this.currentPage > 3) {
        pages.push('...');
      }
      
      // Calculate range around current page
      let start = Math.max(2, this.currentPage - 1);
      let end = Math.min(this.totalPages - 1, this.currentPage + 1);
      
      // Adjust if at start or end
      if (this.currentPage <= 3) {
        end = Math.min(4, this.totalPages - 1);
      }
      if (this.currentPage >= this.totalPages - 2) {
        start = Math.max(2, this.totalPages - 3);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (this.currentPage < this.totalPages - 2) {
        pages.push('...');
      }
      
      // Always show last page
      if (this.totalPages > 1) {
        pages.push(this.totalPages);
      }
    }
    
    return pages;
  }

  /**
   * Handle sorting
   */
  onSortChange(sortIndex: number, sort: SortOrder): void {
    this.filters.sortIndex = sortIndex;
    this.filters.sort = sort;
    this.loadInvoices();
    this.syncFiltersToUrl();
  }

  /**
   * Get invoice type label
   */
  getInvoiceTypeLabel(invoiceType: number): string {
    const type = this.invoiceTypes.find((t) => t.id === invoiceType.toString());
    if (!type) return invoiceType.toString();

    if (this.curentLang === 'ar') return type.name_ar;
    if (this.curentLang === 'he') return type.name_he;
    return type.name_en;
  }

  /**
   * Receipt (type 400) has no items; its total is stored in total_amount (sum of payment methods).
   * All other types use payment_amount_including_vat (sum of item totals).
   */
  getDisplayAmount(invoice: any): number {
    if (!invoice) return 0;
    if (+invoice.invoice_type === 400) {
      return +invoice.total_amount || 0;
    }
    return +invoice.payment_amount_including_vat || 0;
  }

  getDocumentCurrencyCode(invoice: any): string {
    return resolveDocumentCurrency(invoice ?? {});
  }

  isForeignCurrencyInvoice(invoice: any): boolean {
    return isForeignDocument(invoice ?? {});
  }

  getDocumentCurrencyDisplay(invoice: any): string {
    return this.getDocumentCurrencyCode(invoice);
  }

  getDocumentCurrencySymbolForRow(invoice: any): string {
    return getDocumentCurrencySymbol(invoice ?? {});
  }

  /**
   * "Create reverse" is shown for any confirmed, non-deleted row where procedure !== 3
   * (`procedure === 3` = cancelled / إلغاء في مسار الهيئة — see view component).
   * There is no extra block on invoice type 330 (فاتورة عكسية); if the menu is missing for those rows,
   * check API fields `procedure` / `is_deleted`. Chaining reverse (عكس على عكس) may still be rejected by the server.
   */
  canCreateReverseInvoice(item: any): boolean {
    if (!item) return false;
    if (item.confirm !== 1 || item.is_deleted === 1) return false;
    const proc = item.procedure;
    if (proc === 3 || proc === '3') return false;
    return true;
  }

  /**
   * GET /invoices-v2/integration/send-second/:id — same rules as API doc + list row context
   * (confirmed, document_number set, no confirmation_number, not deleted/cancelled/hearing-only).
   */
  canResendTaxAuthoritySecond(item: any): boolean {
    return computeProcedureActionFlags(item).canResendSecond;
  }

  itemIsInProcedureCycle(item: any): boolean {
    return isTruthyDbFlag(item?.is_in_procedure_cycle);
  }

  getProcedureLabelForItem(item: any): string {
    return getProcedureLabel(item?.procedure, this.curentLang);
  }

  getPaymentStatusLabel(item: any): string | null {
    if (!shouldShowPaymentTrackingInUi(item)) {
      return null;
    }
    return getInvoicePaymentStatusLabel(item, this.curentLang);
  }

  getPaymentStatusBadgeClass(item: any): string {
    if (!shouldShowPaymentTrackingInUi(item)) {
      return '';
    }
    return getEffectivePaymentStatusBadgeClass(
      computeEffectivePaymentStatus(item?.payment_status, item?.due_date),
    );
  }

  paymentStatusFilterLabel(status: EffectivePaymentStatus): string {
    return this.translate.instant(`invoices_v2.payment.filter_${status}`);
  }

  isPaymentTrackingInvoice(item: any): boolean {
    return isPaymentTrackingInvoiceType(item?.invoice_type);
  }

  showPaymentTrackingInUi(item: any): boolean {
    return shouldShowPaymentTrackingInUi(item);
  }

  canUpdatePaymentFromList(item: any): boolean {
    return (
      !!item &&
      item.confirm === 1 &&
      item.is_deleted !== 1 &&
      isPaymentTrackingInvoiceType(item.invoice_type)
    );
  }

  isSavingPaymentFor(invoiceId: number): boolean {
    return this.savingPaymentInvoiceId === invoiceId;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (
      target?.closest(
        '.payment-due-popover, .payment-status-popover, .payment-status-badge--editable, .payment-inline-wrap, .payment-inline-editor, .payment-inline-trigger, .payment-inline-cell, .p-calendar, .p-datepicker, .p-datepicker-panel, .p-datepicker-header, .p-datepicker-calendar, .p-datepicker-buttonbar, .p-monthpicker, .p-yearpicker, .p-link',
      )
    ) {
      return;
    }
    this.closeInlinePaymentEditors();
  }

  @HostListener('window:scroll')
  @HostListener('window:resize')
  onWindowScrollOrResize(): void {
    if (this.editingDueDateInvoiceId != null && this.dueDateAnchorEl) {
      this.positionDueDatePopover(this.dueDateAnchorEl);
      this.scheduleDueDatePopoverPosition();
    }
    if (this.openPaymentStatusMenuId != null && this.paymentStatusAnchorEl) {
      this.positionPaymentStatusMenu(this.paymentStatusAnchorEl);
    }
  }

  private positionPaymentStatusMenu(anchor: HTMLElement): void {
    const rect = anchor.getBoundingClientRect();
    const width = InvoicesV2ListComponent.PAYMENT_STATUS_MENU_WIDTH;
    const height = InvoicesV2ListComponent.PAYMENT_STATUS_MENU_HEIGHT;
    const margin = 8;
    const gap = 4;

    let left = rect.left;
    if (document.documentElement.dir === 'rtl') {
      left = rect.right - width;
    }
    left = Math.max(margin, Math.min(left, window.innerWidth - width - margin));

    const spaceBelow = window.innerHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    let top: number;
    if (spaceBelow >= height + gap || spaceBelow >= spaceAbove) {
      top = rect.bottom + gap;
    } else {
      top = rect.top - height - gap;
    }
    top = Math.max(margin, Math.min(top, window.innerHeight - height - margin));

    this.paymentStatusMenuStyle = {
      top: `${top}px`,
      left: `${left}px`,
    };
  }

  private closePaymentStatusMenu(): void {
    this.openPaymentStatusMenuId = null;
    this.openPaymentStatusMenuItem = null;
    this.paymentStatusMenuStyle = null;
    this.paymentStatusAnchorEl = null;
  }

  private positionDueDatePopover(anchor: HTMLElement): void {
    const rect = anchor.getBoundingClientRect();
    const width = InvoicesV2ListComponent.DUE_DATE_POPOVER_WIDTH;
    const margin = 8;
    const gap = 6;
    let left = rect.left;
    if (document.documentElement.dir === 'rtl') {
      left = rect.right - width;
    }
    left = Math.max(margin, Math.min(left, window.innerWidth - width - margin));

    const popoverEl = this.dueDatePopoverRef?.nativeElement;
    const popoverHeight =
      popoverEl?.offsetHeight || InvoicesV2ListComponent.DUE_DATE_POPOVER_ESTIMATED_HEIGHT;
    const maxPopoverHeight = window.innerHeight - margin * 2;
    const effectiveHeight = Math.min(popoverHeight, maxPopoverHeight);

    const spaceBelow = window.innerHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    let top: number;
    if (spaceBelow >= effectiveHeight + gap || spaceBelow >= spaceAbove) {
      top = rect.bottom + gap;
    } else {
      top = rect.top - effectiveHeight - gap;
    }
    top = Math.max(margin, Math.min(top, window.innerHeight - effectiveHeight - margin));

    this.dueDatePopoverStyle = {
      top: `${top}px`,
      left: `${left}px`,
      maxHeight: `${maxPopoverHeight}px`,
    };
  }

  private scheduleDueDatePopoverPosition(): void {
    if (!this.dueDateAnchorEl) {
      return;
    }
    const anchor = this.dueDateAnchorEl;
    setTimeout(() => {
      if (this.editingDueDateInvoiceId != null && this.dueDateAnchorEl === anchor) {
        this.positionDueDatePopover(anchor);
      }
    }, 0);
  }

  private focusDueDatePopoverForRow(invoiceId: number): void {
    const anchor = document.querySelector(
      `tr[data-invoice-row-id="${invoiceId}"] .payment-inline-trigger`,
    ) as HTMLElement | null;
    if (!anchor) {
      return;
    }
    this.dueDateAnchorEl = anchor;
    anchor.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    this.positionDueDatePopover(anchor);
    this.scheduleDueDatePopoverPosition();
  }

  closeInlinePaymentEditors(): void {
    this.closePaymentStatusMenu();
    this.editingDueDateInvoiceId = null;
    this.editingDueDateItem = null;
    this.dueDatePopoverStyle = null;
    this.dueDateAnchorEl = null;
    this.pendingUnpaidInvoiceId = null;
    this.dueDateEditValue = null;
  }

  togglePaymentStatusMenu(item: any, event: Event): void {
    event.stopPropagation();
    if (!this.canUpdatePaymentFromList(item) || this.isSavingPaymentFor(item.invoice_id)) {
      return;
    }
    this.editingDueDateInvoiceId = null;
    this.editingDueDateItem = null;
    this.dueDatePopoverStyle = null;
    this.dueDateAnchorEl = null;
    this.dueDateEditValue = null;
    this.pendingUnpaidInvoiceId = null;

    if (this.openPaymentStatusMenuId === item.invoice_id) {
      this.closePaymentStatusMenu();
      return;
    }

    const anchor = event.currentTarget as HTMLElement;
    this.openPaymentStatusMenuId = item.invoice_id;
    this.openPaymentStatusMenuItem = item;
    this.paymentStatusAnchorEl = anchor;
    anchor.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    this.positionPaymentStatusMenu(anchor);
    setTimeout(() => {
      if (this.openPaymentStatusMenuId === item.invoice_id && this.paymentStatusAnchorEl === anchor) {
        this.positionPaymentStatusMenu(anchor);
      }
    }, 0);
  }

  selectPaymentStatus(item: any, status: StoredPaymentStatus, event: Event): void {
    event.stopPropagation();
    if (!this.canUpdatePaymentFromList(item)) return;
    this.closePaymentStatusMenu();

    if (needsDueDateBeforeUnpaid(item, status)) {
      this.pendingUnpaidInvoiceId = item.invoice_id;
      this.editingDueDateInvoiceId = item.invoice_id;
      this.editingDueDateItem = item;
      this.dueDateEditValue = null;
      this.toastr.info(this.translate.instant('invoices_v2.payment.select_due_date_for_unpaid'));
      setTimeout(() => this.focusDueDatePopoverForRow(item.invoice_id), 0);
      return;
    }

    if (shouldSkipPaymentStatusPatch(item, status)) {
      return;
    }
    this.patchInvoicePaymentInline(item, buildPaymentStatusPatchBody(item, status));
  }

  startDueDateEdit(item: any, event: Event): void {
    event.stopPropagation();
    if (!this.canUpdatePaymentFromList(item) || this.isSavingPaymentFor(item.invoice_id)) {
      return;
    }
    this.closePaymentStatusMenu();
    this.pendingUnpaidInvoiceId = null;
    this.editingDueDateInvoiceId = item.invoice_id;
    this.editingDueDateItem = item;
    this.dueDateEditValue = parseDueDateFromApi(item.due_date) ?? new Date();
    const anchor =
      ((event.currentTarget as HTMLElement)?.closest('.payment-inline-trigger') as HTMLElement | null) ??
      (event.currentTarget as HTMLElement);
    this.dueDateAnchorEl = anchor;
    anchor.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    this.positionDueDatePopover(anchor);
    this.scheduleDueDatePopoverPosition();
  }

  cancelDueDateEdit(event?: Event): void {
    event?.stopPropagation();
    this.editingDueDateInvoiceId = null;
    this.editingDueDateItem = null;
    this.dueDatePopoverStyle = null;
    this.dueDateAnchorEl = null;
    this.pendingUnpaidInvoiceId = null;
    this.dueDateEditValue = null;
  }

  saveDueDateInline(item: any, event?: Event): void {
    event?.stopPropagation();
    if (!this.canUpdatePaymentFromList(item) || this.editingDueDateInvoiceId !== item.invoice_id) {
      return;
    }
    const formatted = formatDueDateForApi(this.dueDateEditValue);

    if (this.pendingUnpaidInvoiceId === item.invoice_id) {
      if (!formatted) {
        this.toastr.warning(this.translate.instant('invoices_v2.payment.due_date_required_for_unpaid'));
        return;
      }
      this.pendingUnpaidInvoiceId = null;
      this.patchInvoicePaymentInline(item, buildUnpaidWithDueDatePatchBody(this.dueDateEditValue));
      return;
    }

    const current = item.due_date ? String(item.due_date).slice(0, 10) : null;
    if (formatted === current) {
      this.cancelDueDateEdit();
      return;
    }
    this.patchInvoicePaymentInline(item, { due_date: formatted });
  }

  clearDueDateInline(item: any, event: Event): void {
    event.stopPropagation();
    if (!this.canUpdatePaymentFromList(item)) return;
    if (item.due_date == null || item.due_date === '') {
      this.cancelDueDateEdit();
      return;
    }
    this.patchInvoicePaymentInline(item, buildClearDueDatePatchBody(item));
  }

  private patchInvoicePaymentInline(item: any, body: UpdateInvoicePaymentBody): void {
    if (this.savingPaymentInvoiceId != null) return;

    const snapshot = {
      payment_status: item.payment_status,
      due_date: item.due_date,
      effective_payment_status: item.effective_payment_status,
      payment_status_label_ar: item.payment_status_label_ar,
      payment_status_label_en: item.payment_status_label_en,
      payment_status_label_he: item.payment_status_label_he,
      updatedDate: item.updatedDate,
      updated_by: item.updated_by,
    };

    applyPaymentFieldsToInvoiceRow(item, body);
    this.savingPaymentInvoiceId = item.invoice_id;

    this.invoiceV2Service.updateInvoicePayment(item.invoice_id, body).subscribe({
      next: (response) => {
        this.savingPaymentInvoiceId = null;
        if (response?.data) {
          applyPaymentFieldsToInvoiceRow(
            item,
            body,
            response.data as unknown as Record<string, unknown>,
          );
        }
        this.toastr.success(
          this.getApiMessage(response) ||
            this.translate.instant('invoices_v2.payment.update_success'),
        );
        this.closeInlinePaymentEditors();
      },
      error: (error) => {
        this.savingPaymentInvoiceId = null;
        Object.assign(item, snapshot);
        this.toastr.error(
          this.getApiErrorMessage(
            error,
            this.translate.instant('invoices_v2.payment.update_error'),
          ),
        );
      },
    });
  }

  openProcedureModal(item: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (!item?.invoice_id) return;
    this.procedureModalInvoice = normalizeProcedureInvoiceRow(item as Record<string, unknown>);
    this.procedureModalInvoiceId = item.invoice_id;
    this.activeProcedureModal = this.modalService.open(this.procedureModalTpl, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
    });
  }

  closeProcedureModal(): void {
    this.activeProcedureModal?.close();
    this.activeProcedureModal = null;
    this.procedureModalInvoice = null;
    this.procedureModalInvoiceId = null;
  }

  onProcedureActionCompleted(): void {
    this.closeProcedureModal();
    this.loadInvoices();
  }

  /**
   * After 405 confirm: reload list from API then open procedure modal with normalized row.
   */
  private refreshInvoiceRowAfterProcedureEntry(invoiceId: number): void {
    this.loadInvoices();
    this.invoiceV2Service.getInvoiceById(invoiceId).subscribe({
      next: (inv) => {
        const data = normalizeProcedureInvoiceRow((inv?.data ?? inv) as Record<string, unknown>);
        const idx = this.invoicesData.findIndex((i) => i.invoice_id === invoiceId);
        if (idx >= 0) {
          this.invoicesData[idx] = { ...this.invoicesData[idx], ...data };
        }
        if (isTruthyDbFlag(data?.is_in_procedure_cycle)) {
          this.openProcedureModal(data);
        }
      },
    });
  }

  /**
   * Resend confirmed invoice to tax authority when first submission failed (no confirmation_number yet).
   */
  resendTaxAuthoritySecond(invoiceId: number): void {
    Swal.fire({
      title: this.translate.instant('invoices_v2.list.resend_tax_second_confirm_title'),
      text: this.translate.instant('invoices_v2.list.resend_tax_second_confirm_text'),
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d',
      confirmButtonText:
        this.curentLang === 'ar' ? 'إرسال' : this.curentLang === 'he' ? 'שלח' : 'Send',
      cancelButtonText:
        this.curentLang === 'ar' ? 'إلغاء' : this.curentLang === 'he' ? 'ביטול' : 'Cancel',
    }).then((result) => {
      if (!result.isConfirmed) return;
      this.spinner.show();
      this.invoiceV2Service.sendInvoiceSecond(invoiceId).subscribe({
        next: (response: any) => {
          this.spinner.hide();
          if (response?.status === 200) {
            this.toastr.success(this.getApiMessage(response));
            this.loadInvoices();
          } else {
            this.toastr.warning(this.getApiMessage(response));
          }
        },
        error: (error) => {
          this.spinner.hide();
          this.toastr.error(
            this.getApiErrorMessage(
              error,
              this.curentLang === 'ar'
                ? 'تعذر إعادة الإرسال لمصلحة الضرائب'
                : this.curentLang === 'he'
                  ? 'לא ניתן לשלוח שוב לרשות המסים'
                  : 'Could not resend to tax authority',
            ),
          );
        },
      });
    });
  }

  /**
   * Check if invoice type can be converted
   */
  canConvertInvoice(invoiceType: number): boolean {
    const type = this.invoiceTypes.find((t) => t.id === invoiceType.toString());
    return type?.can_convert === true || type?.can_convert === 1;
  }

  /**
   * Get convert to type name
   */
  getConvertToTypeName(invoiceType: number): string {
    const type = this.invoiceTypes.find((t) => t.id === invoiceType.toString());
    if (!type?.convert_to_invoice_type_code) return '';
    
    const convertToType = this.invoiceTypes.find((t) => t.id === type.convert_to_invoice_type_code.toString());
    if (!convertToType) return '';
    
    if (this.curentLang === 'ar') return convertToType.name_ar;
    if (this.curentLang === 'he') return convertToType.name_he;
    return convertToType.name_en;
  }

  /**
   * Convert invoice to another type
   */
  convertInvoice(invoiceId: number, invoiceType: number): void {
    const type = this.invoiceTypes.find((t) => t.id === invoiceType.toString());
    const convertToName = this.getConvertToTypeName(invoiceType);
    
    Swal.fire({
      title:
        this.curentLang === 'ar'
          ? 'تحويل الفاتورة'
          : this.curentLang === 'he'
            ? 'המרת חשבונית'
            : 'Convert Invoice',
      text:
        this.curentLang === 'ar'
          ? `هل أنت متأكد من تحويل هذه الفاتورة إلى ${convertToName}؟`
          : this.curentLang === 'he'
            ? `האם אתה בטוח שברצונך להמיר חשבונית זו ל${convertToName}?`
            : `Are you sure you want to convert this invoice to ${convertToName}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#17a2b8',
      cancelButtonColor: '#6c757d',
      confirmButtonText:
        this.curentLang === 'ar' ? 'تحويل' : this.curentLang === 'he' ? 'המר' : 'Convert',
      cancelButtonText:
        this.curentLang === 'ar' ? 'إلغاء' : this.curentLang === 'he' ? 'ביטול' : 'Cancel',
    }).then((result) => {
      if (result.isConfirmed) {
        this.spinner.show();
        this.invoiceV2Service.convertInvoice(invoiceId).subscribe({
          next: (response: any) => {
            this.spinner.hide();
            const message = this.curentLang === 'ar'
              ? response.messageAr || 'تم تحويل الفاتورة بنجاح'
              : this.curentLang === 'he'
                ? response.messageHe || 'החשבונית הומרה בהצלחה'
                : response.messageEn || 'Invoice converted successfully';
            this.toastr.success(message);
            this.loadInvoices();
          },
          error: (error) => {
            this.spinner.hide();
            this.toastr.error(this.getApiErrorMessage(error, 'Error converting invoice'));
          },
        });
      }
    });
  }

  /**
   * Toggle collapse for invoice details
   */
  toggleCollapse(index: number): void {
    this.openedIndex = this.openedIndex === index ? null : index;
  }

  /**
   * Toggle collapse for actions
   */
  toggleCollapse2(index: number): void {
    this.openedIndex2 = this.openedIndex2 === index ? null : index;
  }

  /**
   * View invoice (using state - no ID in URL for security)
   */
  viewInvoice(invoiceId: number): void {
    this.navigateToInvoiceView(invoiceId, false);
  }

  private navigateToInvoiceView(invoiceId: number, openProcedureModal = false): void {
    const listQueryParams = this.buildFilterQueryParams();
    void this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: listQueryParams,
        replaceUrl: false,
      })
      .then(() => {
        this.router.navigate(['/dashboard/invoices-v2/view'], {
          queryParams: { invoiceId },
          state: { invoiceId, openProcedureModal, listQueryParams },
        });
      });
  }

  /**
   * After returning with ?highlightInvoiceId=, scroll to and pulse-highlight the row.
   */
  private maybeHighlightInvoiceRow(): void {
    const id = this.pendingHighlightInvoiceId;
    if (id == null) {
      return;
    }

    this.pendingHighlightInvoiceId = null;
    this.clearHighlightQueryParam();

    const row = this.invoicesData.find((i) => i.invoice_id === id);
    if (!row) {
      return;
    }

    this.highlightedInvoiceId = id;

    setTimeout(() => {
      const el = document.querySelector(`[data-invoice-row-id="${id}"]`);
      el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 100);

    if (this.highlightClearTimer) {
      clearTimeout(this.highlightClearTimer);
    }
    this.highlightClearTimer = setTimeout(() => {
      this.highlightedInvoiceId = null;
      this.highlightClearTimer = null;
    }, 5000);
  }

  /** Remove highlight query param so refresh/navigation keeps current list filters/sort. */
  private clearHighlightQueryParam(): void {
    if (!this.route.snapshot.queryParamMap.has('highlightInvoiceId')) {
      return;
    }
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { highlightInvoiceId: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /**
   * Edit invoice
   */
  editInvoice(invoiceId: number): void {
    this.router.navigate(['/dashboard/invoices-v2/edit', invoiceId]);
  }

  /**
   * Delete invoice
   */
  deleteInvoice(invoiceId: number): void {
    Swal.fire({
      title: this.curentLang === 'ar' ? 'تأكيد الحذف' : this.curentLang === 'he' ? 'אישור מחיקה' : 'Confirm Delete',
      text:
        this.curentLang === 'ar'
          ? 'هل أنت متأكد من حذف هذه الفاتورة؟'
          : this.curentLang === 'he'
            ? 'האם אתה בטוח שברצונך למחוק חשבונית זו?'
            : 'Are you sure you want to delete this invoice?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText:
        this.curentLang === 'ar' ? 'حذف' : this.curentLang === 'he' ? 'מחק' : 'Delete',
      cancelButtonText:
        this.curentLang === 'ar' ? 'إلغاء' : this.curentLang === 'he' ? 'ביטול' : 'Cancel',
    }).then((result) => {
      if (result.isConfirmed) {
        this.spinner.show();
        this.invoiceV2Service.deleteInvoice(invoiceId).subscribe({
          next: () => {
            this.spinner.hide();
            this.toastr.success(
              this.curentLang === 'ar'
                ? 'تم حذف الفاتورة بنجاح'
                : this.curentLang === 'he'
                  ? 'החשבונית נמחקה בהצלחה'
                  : 'Invoice deleted successfully',
            );
            this.loadInvoices();
          },
          error: (error) => {
            this.spinner.hide();
            this.toastr.error(this.getApiErrorMessage(error, 'Error deleting invoice'));
          },
        });
      }
    });
  }

  /**
   * Copy invoice - navigates to create page with pre-filled data
   */
  copyInvoice(invoiceId: number): void {
    this.spinner.show();
    this.invoiceV2Service.getInvoiceById(invoiceId).subscribe({
      next: (response: any) => {
        this.spinner.hide();
        const invoice = response.data || response;
        const docCurrency = resolveDocumentCurrency(invoice);
        const foreignCurrency = resolveForeignCurrencyFromInvoice(invoice);

        // Prepare copy data (remove IDs and dates that should be new)
        const copyData = {
          company_id: invoice.company_id,
          customer_id: invoice.customer_id,
          invoice_type: invoice.invoice_type,
          document_language: invoice.document_language,
          invoice_note: invoice.invoice_note,
          allocation_number: invoice.allocation_number ?? 0,
          tax_status: invoice.tax_status,
          due_date: invoice.due_date ?? null,
          payment_status: invoice.payment_status ?? 'paid',
          currency: docCurrency,
          exchange_rate: foreignCurrency?.exchange_rate ?? 1,
          exchange_rate_date: foreignCurrency?.exchange_rate_date ?? '',
          exchange_rate_source: foreignCurrency?.exchange_rate_source ?? 'BOI',
          exchange_rate_manually_changed: !!foreignCurrency?.exchange_rate_manually_changed,
          // Copy items with all required fields (ILS + foreign when applicable)
          items: invoice.soldItems?.map((item: any) => {
            const foreignAmounts = readSoldItemForeignFromApi(item);
            return {
              product_id: item.product_id,
              item_name: item.item_name || item.description || '',
              description: item.description || '',
              catalog_id: item.catalog_id || '',
              category: item.category || '',
              measurement_unit_id: item.measurement_unit_id || null,
              measure_unit_description: item.measure_unit_description || '',
              quantity: item.quantity || 1,
              price_per_unit: item.price_per_unit || 0,
              discount: item.discount || 0,
              vat_rate: item.vat_rate || 17,
              sub_total: item.sub_total || 0,
              vat_amount: item.vat_amount || 0,
              total_amount: item.total_amount || 0,
              isPresenting: item.isPresenting || false,
              isVatIncluded: item.isVatIncluded || false,
              ...foreignAmounts,
            };
          }) || [],
          // Copy payment methods without IDs
          payment_methods: invoice.receiptsDetails?.map((pm: any) => ({
            type: pm.payment_type,
            amount: pm.amount,
            details: {
              reference: pm.reference,
              bank: pm.bank,
              branch: pm.branch,
              account: pm.account,
              details: pm.details,
              credit_card_code: pm.credit_card_code,
              credit_card_name: pm.credit_card_name,
              credit_type: pm.credit_type,
              account_number: pm.account_number,
            },
          })) || [],
        };

        // Navigate to create page with copy data
        this.router.navigate(['/dashboard/invoices-v2/create'], {
          state: { copyData: copyData },
        });

        this.toastr.info(
          this.curentLang === 'ar'
            ? 'تم نسخ بيانات الفاتورة'
            : this.curentLang === 'he'
              ? 'נתוני החשבונית הועתקו'
              : 'Invoice data copied',
        );
      },
      error: (error) => {
        this.spinner.hide();
        this.toastr.error(this.getApiErrorMessage(error, 'Error copying invoice'));
      },
    });
  }

  /**
   * Confirm invoice
   */
  confirmInvoice(invoiceId: number): void {
    Swal.fire({
      title:
        this.curentLang === 'ar'
          ? 'تأكيد الفاتورة'
          : this.curentLang === 'he'
            ? 'אישור חשבונית'
            : 'Confirm Invoice',
      text:
        this.curentLang === 'ar'
          ? 'هل أنت متأكد من تأكيد هذه الفاتورة؟'
          : this.curentLang === 'he'
            ? 'האם אתה בטוח שברצונך לאשר חשבונית זו?'
            : 'Are you sure you want to confirm this invoice?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d',
      confirmButtonText:
        this.curentLang === 'ar' ? 'تأكيد' : this.curentLang === 'he' ? 'אשר' : 'Confirm',
      cancelButtonText:
        this.curentLang === 'ar' ? 'إلغاء' : this.curentLang === 'he' ? 'ביטול' : 'Cancel',
    }).then((result) => {
      if (result.isConfirmed) {
        this.spinner.show();
        this.invoiceV2Service.confirmInvoice(invoiceId).subscribe({
          next: (response: MultiLangMessagePayload | any) => {
            this.spinner.hide();
            if (response && response.success === false) {
              this.handleConfirmError(invoiceId, response);
              return;
            }

            if (isProcedureCycleConfirmResponse(response)) {
              const msg = this.getApiMessage(response);
              Swal.fire({
                icon: 'warning',
                title: msg,
                text: this.translate.instant('invoices_v2.procedure.confirm_405_hint'),
                confirmButtonColor: '#E62E2D',
              }).then(() => {
                this.navigateToInvoiceView(invoiceId, true);
              });
              return;
            }

            const msg = this.getApiMessage(response);
            this.toastr.success(msg);
            this.navigateToInvoiceView(invoiceId, false);
          },
          error: (error) => {
            this.spinner.hide();
            this.handleConfirmError(invoiceId, error?.error);
          },
        });
      }
    });
  }

  /**
   * Get localized error message from API error response
   */
  private getApiErrorMessage(error: any, fallback: string): string {
    const e = error?.error;
    if (!e) return fallback;
    // Nest BadRequestException({ messageAr, ... }) exposes fields under body.message — not top-level.
    if (Array.isArray(e.message)) return e.message.join(' | ');
    const nestedObj =
      e.message != null &&
      typeof e.message === 'object' &&
      !Array.isArray(e.message)
        ? e.message
        : null;
    const src = nestedObj ?? e;
    const msgAr = src.messageAr ?? src.messageAR;
    const msgEn = src.messageEn ?? src.messageEN;
    const msgHe = src.messageHe ?? src.messageHE;
    if (this.curentLang === 'ar' && msgAr) return String(msgAr);
    if (this.curentLang === 'he' && msgHe) return String(msgHe);
    if (msgEn) return String(msgEn);
    if (msgAr) return String(msgAr);
    if (msgHe) return String(msgHe);
    if (typeof e.message === 'string' && e.message.trim()) return e.message;
    return fallback;
  }

  /**
   * Get localized message from API response (list component)
   */
  private getApiMessage(response: any): string {
    if (!response) {
      return this.curentLang === 'ar'
        ? 'تمت العملية بنجاح'
        : this.curentLang === 'he'
          ? 'הפעולה בוצעה בהצלחה'
          : 'Operation completed successfully';
    }

    const msgAr = response.messageAr || response.messageAR;
    const msgEn = response.messageEn || response.messageEN;
    const msgHe = response.messageHe || response.messageHE;
    const dataMsg = response.data?.message && typeof response.data.message === 'string'
      ? response.data.message.trim()
      : null;

    let base = '';
    if (this.curentLang === 'ar' && msgAr) base = msgAr;
    else if (this.curentLang === 'he' && msgHe) base = msgHe;
    else if (msgEn) base = msgEn;
    else base = response.message ||
      (this.curentLang === 'ar'
        ? 'تمت العملية بنجاح'
        : this.curentLang === 'he'
          ? 'הפעולה בוצעה בהצלחה'
          : 'Operation completed successfully');

    return dataMsg ? `${base} — ${dataMsg}` : base;
  }

  /**
   * Handle confirm errors including VAT-related status codes and open VAT modal
   */
  private handleConfirmError(invoiceId: number, response: MultiLangMessagePayload | any): void {
    const msg = this.getApiMessage(response);
    this.toastr.error(msg);

    if (Array.isArray(response?.errors)) {
      response.errors.forEach((e: string) => this.toastr.warning(e));
    }

    const statusCode: InvoiceStatusCode | undefined = response?.statusCode;
    if (
      statusCode === InvoiceStatusCode.CUSTOMER_VAT_REQUIRED ||
      statusCode === InvoiceStatusCode.CUSTOMER_VAT_INVALID ||
      statusCode === InvoiceStatusCode.TAX_AUTHORITY_ERROR
    ) {
      this.openUpdateCustomerVatModalForInvoice(invoiceId);
    }
  }

  /**
   * Fetch invoice -> customer and open VAT/ID update modal
   */
  private openUpdateCustomerVatModalForInvoice(invoiceId: number): void {
    this.invoiceV2Service.getInvoiceById(invoiceId).subscribe({
      next: (invoice: any) => {
        const customerId =
          invoice?.customer_id ||
          invoice?.customer?.customer_id ||
          (Array.isArray(invoice?.customer) ? invoice.customer[0]?.customer_id : null);

        if (!customerId) {
          this.toastr.error(
            this.curentLang === 'ar'
              ? 'لم يتم العثور على عميل مرتبط بهذه الفاتورة'
              : this.curentLang === 'he'
                ? 'לא נמצא לקוח המשויך לחשבונית זו'
                : 'No customer associated with this invoice',
          );
          return;
        }

        this.httpHelpService.getCustomerById(customerId).subscribe({
          next: (customer: any) => {
            const currentWorkerNumber =
              customer?.customer_vat_number ||
              customer?.vat_number ||
              customer?.customer?.customer_vat_number ||
              '';

            openCustomerWorkerNumberSwal(this.curentLang, currentWorkerNumber || '').then((result) => {
              if (result.isConfirmed) {
                const newWorkerNumber = (result.value as any).workerNumber;
                const userId = JSON.parse(localStorage.getItem('user_id') || '0');
                const payload = {
                  ...customer,
                  customer_vat_number: newWorkerNumber,
                  customer_vat_tax: newWorkerNumber,
                  customer_address_zip_code: newWorkerNumber,
                  id_number: newWorkerNumber,
                  updated_by: userId || customer.created_by,
                };
                this.httpHelpService.updateCustomersById(customerId, payload).subscribe({
                  next: () => {
                    this.toastr.success(
                      this.curentLang === 'ar'
                        ? 'تم تحديث رقم المشتغل بنجاح'
                        : this.curentLang === 'he'
                          ? 'מספר העוסק עודכן בהצלחה'
                          : 'Customer worker number updated successfully',
                    );
                  },
                  error: () => {
                    this.toastr.error(
                      this.curentLang === 'ar'
                        ? 'فشل تحديث رقم المشتغل'
                        : this.curentLang === 'he'
                          ? 'עדכון מספר העוסק נכשל'
                          : 'Failed to update customer worker number',
                    );
                  },
                });
              }
            });
          },
          error: () => {
            this.toastr.error(
              this.curentLang === 'ar'
                ? 'فشل تحميل بيانات الزبون لتحديث البيانات الضريبية'
                : this.curentLang === 'he'
                  ? 'טעינת נתוני הלקוח לצורך עדכון המס נכשלה'
                  : 'Failed to load customer data for tax update',
            );
          },
        });
      },
      error: () => {
        this.toastr.error(
          this.curentLang === 'ar'
            ? 'فشل تحميل الفاتورة للحصول على بيانات الزبون'
            : this.curentLang === 'he'
              ? 'טעינת החשבונית לקבלת נתוני הלקוח נכשלה'
              : 'Failed to load invoice to get customer data',
        );
      },
    });
  }

  /**
   * Generate PDF – open only the S3 PDF link (e.g. https://thinvoice.s3.amazonaws.com/files-dev/...), not view page URL.
   */
  generatePdf(invoiceId: number, lang?: string): void {
    this.spinner.show();
    const docLang =
      lang === 'ar' || lang === 'he' || lang === 'en'
        ? lang
        : this.curentLang;
    this.invoiceV2Service.generatePdf(invoiceId, docLang).subscribe({
      next: (response) => {
        this.spinner.hide();
        const pdfLink = (response && (response as any).data != null ? (response as any).data?.pdf_link : (response as any)?.pdf_link) as string | undefined;
        const isS3 = pdfLink && !/invoices-v2\/view|localhost/i.test(pdfLink) && /thinvoice\.s3\.amazonaws\.com|\.s3\.(.*\.)?amazonaws\.com|https:\/\/.*\.pdf$/i.test(pdfLink);
        if (isS3 && pdfLink) {
          window.open(pdfLink, '_blank');
          this.toastr.success(
            this.curentLang === 'ar'
              ? 'تم توليد PDF بنجاح'
              : this.curentLang === 'he'
                ? 'PDF נוצר בהצלחה'
                : 'PDF generated successfully',
          );
          this.loadInvoices(); // Reload to get updated pdf_link
        } else if (pdfLink) {
          this.toastr.warning(this.curentLang === 'ar' ? 'لم يتم الحصول على رابط تحميل PDF صحيح.' : 'Could not get valid PDF download link.');
        }
      },
      error: (error) => {
        this.spinner.hide();
          this.toastr.error(this.getApiErrorMessage(error, 'Error generating PDF'));
      },
    });
  }

  /**
   * Create reverse invoice
   */
  createReverseInvoice(invoiceId: number): void {
    Swal.fire({
      title:
        this.curentLang === 'ar'
          ? 'إنشاء مستند عكسية'
          : this.curentLang === 'he'
            ? 'יצירת חשבונית הפוכה'
            : 'Create Reverse Invoice',
      text:
        this.curentLang === 'ar'
          ? 'هل أنت متأكد من إنشاء مستند عكسية لهذه الفاتورة؟'
          : this.curentLang === 'he'
            ? 'האם אתה בטוח שברצונך ליצור חשבונית הפוכה עבור חשבונית זו?'
            : 'Are you sure you want to create a reverse invoice for this invoice?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText:
        this.curentLang === 'ar' ? 'إنشاء' : this.curentLang === 'he' ? 'צור' : 'Create',
      cancelButtonText:
        this.curentLang === 'ar' ? 'إلغاء' : this.curentLang === 'he' ? 'ביטול' : 'Cancel',
    }).then((result) => {
      if (result.isConfirmed) {
        this.spinner.show();
        this.invoiceV2Service.createReverseInvoice(invoiceId).subscribe({
          next: (response) => {
            this.spinner.hide();
            this.toastr.success(this.getApiMessage(response));
            this.loadInvoices();
          },
          error: (error) => {
            this.spinner.hide();
            this.toastr.error(this.getApiErrorMessage(error, 'Error creating reverse invoice'));
          },
        });
      }
    });
  }

  get listSelectionCount(): number {
    if (this.allFilteredSelected) return this.total;
    return this.selectedListIds.length;
  }

  get hasListSelection(): boolean {
    return this.listSelectionCount > 0;
  }

  isListRowSelected(invoiceId: number): boolean {
    return this.allFilteredSelected || this.selectedListIds.includes(invoiceId);
  }

  isAllFilteredSelectedOrPageFullySelected(): boolean {
    if (this.allFilteredSelected) return true;
    if (!this.invoicesData.length) return false;
    return this.invoicesData.every((row) => this.selectedListIds.includes(row.invoice_id));
  }

  isListSelectAllIndeterminate(): boolean {
    if (this.allFilteredSelected || !this.invoicesData.length) return false;
    const selectedOnPage = this.invoicesData.filter((row) =>
      this.selectedListIds.includes(row.invoice_id),
    ).length;
    return selectedOnPage > 0 && selectedOnPage < this.invoicesData.length;
  }

  toggleSelectAllFiltered(ev: Event): void {
    ev.preventDefault();
    ev.stopPropagation();
    if (this.allFilteredSelected) {
      this.clearListSelection();
      return;
    }
    this.allFilteredSelected = true;
    this.selectedListIds = [];
  }

  toggleListInvoiceSelection(invoiceId: number, ev: Event): void {
    ev.stopPropagation();
    const checked = (ev.target as HTMLInputElement).checked;

    if (this.allFilteredSelected) {
      this.allFilteredSelected = false;
      const pageIds = this.invoicesData.map((r) => r.invoice_id as number);
      this.selectedListIds = checked
        ? pageIds
        : pageIds.filter((id) => id !== invoiceId);
      return;
    }

    if (checked) {
      if (!this.selectedListIds.includes(invoiceId)) {
        this.selectedListIds = [...this.selectedListIds, invoiceId];
      }
      if (
        this.total > 0 &&
        this.selectedListIds.length === this.total &&
        this.invoicesData.length === this.total
      ) {
        this.allFilteredSelected = true;
        this.selectedListIds = [];
      }
    } else {
      this.selectedListIds = this.selectedListIds.filter((id) => id !== invoiceId);
    }
  }

  clearListSelection(): void {
    this.selectedListIds = [];
    this.allFilteredSelected = false;
  }

  /**
   * Navigate to create invoice
   */
  createInvoice(): void {
    this.router.navigate(['/dashboard/invoices-v2/create']);
  }

  /**
   * Get status label
   */
  getStatusLabel(invoice: any): string {
    if (invoice.is_deleted === 1) {
      return this.curentLang === 'ar' ? 'ملغاة' : this.curentLang === 'he' ? 'מבוטלת' : 'Deleted';
    }
    if (invoice.confirm === 1) {
      return this.curentLang === 'ar' ? 'مؤكدة' : this.curentLang === 'he' ? 'מאושרת' : 'Confirmed';
    }
    return this.curentLang === 'ar' ? 'المسودّه' : this.curentLang === 'he' ? 'טיוטה' : 'Draft';
  }

  /**
   * Get status badge class
   */
  getStatusBadgeClass(invoice: any): string {
    if (invoice.is_deleted === 1) return 'badge-danger';
    if (invoice.confirm === 1) return 'badge-success';
    return 'badge-warning';
  }
}

